// Pure helpers for the Web scans page: the scan catalog, option fields, request building and JS-discovery exports.
import JSZip from 'jszip';

// Scilla was removed from the platform, so it is not offered.
export const SCAN_GROUPS = [
  { id: 'quick', title: 'Quick checks', items: [
    { value: 'http_basic', label: 'HTTP basic', time: '~2-5 min', detail: 'Fast reachability and basic checks' },
    { value: 'web_check', label: 'Web check', time: '~2-8 min', detail: 'Headers, secrets, stack traces, backups, .git, env files, robots, sitemap and technology hints' },
    { value: 'web_toolkit', label: 'Web toolkit', time: '~8-30 min', detail: 'WAF, technology, TLS, Nikto, HTTPX, Arjun and FFUF when installed' }
  ] },
  { id: 'discovery', title: 'Discovery', items: [
    { value: 'web_crawl', label: 'Web crawl', time: '~5-12 min', detail: 'Katana-powered endpoint discovery' },
    { value: 'web_js_spider', label: 'JS discovery', time: '~8-18 min', detail: 'JavaScript endpoints, parameters, secrets and sensitive paths' },
    { value: 'web_dirsearch', label: 'Directory discovery', time: '~5-20 min', detail: 'Dirsearch-powered path and file discovery' }
  ] },
  { id: 'vuln', title: 'Vulnerabilities', items: [
    { value: 'web_vuln', label: 'Vulnerability scan', time: '~8-20 min', detail: 'Focused vulnerability validation' },
    { value: 'nuclei', label: 'Nuclei full scan', time: '~10-35 min', detail: 'Template-based scan with the Nuclei templates your admin manages' },
    { value: 'web_full', label: 'Full web scan', time: '~15-45 min', detail: 'Deep crawling and vulnerability coverage' }
  ] },
  { id: 'api', title: 'APIs and fuzzing', items: [
    { value: 'api_openapi', label: 'OpenAPI security', time: '~4-10 min', detail: 'Reads the OpenAPI spec and flags authentication, exposure and design weaknesses' },
    { value: 'api_active_security', label: 'API active audit', time: '~5-12 min', detail: 'Auth bypass, BOLA/BFLA heuristics, rate limit and schema drift checks' },
    { value: 'graphql_security', label: 'GraphQL security', time: '~3-8 min', detail: 'Introspection, schema exposure and playground endpoints' },
    { value: 'web_active_fuzz', label: 'Active fuzzing', time: '~4-15 min', detail: 'Mutation-based payload injection and response anomaly detection' }
  ] },
  { id: 'other', title: 'Other', items: [
    { value: 'secrets_git_history', label: 'Secrets git scan', time: '~3-20 min', detail: 'Scans a repository and its git history for leaked secrets' }
  ] }
];
export const SCAN_TYPES = SCAN_GROUPS.flatMap((g) => g.items);
export const SCAN_LABEL = Object.fromEntries(SCAN_TYPES.map((s) => [s.value, s.label]));
export const scanLabel = (type) => SCAN_LABEL[type] || String(type || '-').replaceAll('_', ' ');

// Quick / Balanced / Deep pick a scan type plus a few options.
export const DEPTHS = [
  { id: 'quick', title: 'Quick', desc: 'Is it up, and the obvious problems. Headers, TLS and basic checks.', type: 'http_basic', options: { maxDepth: 2, includeSubdomains: false } },
  { id: 'balanced', title: 'Balanced', desc: 'Crawls the site and checks the endpoints it finds.', type: 'web_crawl', options: { maxDepth: 3, includeSubdomains: false } },
  { id: 'deep', title: 'Deep', desc: 'Also reads JavaScript and includes subdomains. Most thorough.', type: 'web_js_spider', options: { maxDepth: 1, includeSubdomains: true, probeSensitivePaths: true, maxPages: 60, pathLimit: 40 } }
];

export const DEFAULT_OPTIONS = {
  scope: 'single', maxDepth: 3, includeSubdomains: false, probeSensitivePaths: true, maxPages: 40, pathLimit: 30,
  openapiPath: '/openapi.json', graphqlEndpoint: '/graphql', enableOast: false,
  dirsearchRecursive: true, dirsearchExtensions: 'php,aspx,jsp,js,txt,zip,bak,old', dirsearchThreads: 30,
  dirsearchIncludeStatus: '200-399,401,403', dirsearchExcludeStatus: '404,500-999', dirsearchWordlist: '',
  katanaJsCrawl: true, katanaKnownFiles: true, katanaConcurrency: 10, katanaRateLimit: 150, katanaTimeout: 900,
  fuzzType: 'sqli', fuzzMethod: 'GET', fuzzParam: 'q', fuzzPayloads: '', fuzzMaxPayloads: 120,
  secretsRepoPath: '', secretsIncludeHistory: true, secretsCustomPatterns: ''
};

// Advanced option fields shown for each scan type: [key, label, kind, extra]
const CRAWL_FIELDS = [
  ['katanaJsCrawl', 'Crawl JavaScript files', 'bool'], ['katanaKnownFiles', 'Probe known files', 'bool'],
  ['katanaConcurrency', 'Concurrency', 'number'], ['katanaRateLimit', 'Rate limit (per second)', 'number'], ['katanaTimeout', 'Timeout (seconds)', 'number']
];
export const ADVANCED = {
  web_full: [['includeSubdomains', 'Include subdomains', 'bool'], ['maxDepth', 'Crawl depth', 'number', { min: 0, max: 6 }], ...CRAWL_FIELDS],
  web_crawl: [['includeSubdomains', 'Include subdomains', 'bool'], ['maxDepth', 'Crawl depth', 'number', { min: 0, max: 6 }], ...CRAWL_FIELDS],
  web_js_spider: [['includeSubdomains', 'Include subdomains', 'bool'], ['maxDepth', 'Crawl depth', 'number', { min: 0, max: 2 }],
    ['maxPages', 'Max pages', 'number'], ['pathLimit', 'Path limit', 'number'], ['probeSensitivePaths', 'Probe sensitive paths', 'bool']],
  web_dirsearch: [['dirsearchRecursive', 'Recursive', 'bool'], ['dirsearchThreads', 'Threads', 'number'], ['dirsearchExtensions', 'Extensions', 'text'],
    ['dirsearchWordlist', 'Wordlist (optional)', 'text'], ['dirsearchIncludeStatus', 'Include status codes', 'text'], ['dirsearchExcludeStatus', 'Exclude status codes', 'text']],
  api_openapi: [['openapiPath', 'OpenAPI path', 'text']],
  api_active_security: [['openapiPath', 'Base URL (optional)', 'text']],
  graphql_security: [['graphqlEndpoint', 'GraphQL endpoint', 'text']],
  web_active_fuzz: [['fuzzType', 'Fuzz type', 'select', { options: ['sqli', 'xss', 'ssrf', 'ssti', 'cmdi', 'path_traversal', 'xxe', 'idor'] }],
    ['fuzzMethod', 'Method', 'select', { options: ['GET', 'POST', 'PUT', 'PATCH', 'DELETE'] }], ['fuzzParam', 'Parameter name', 'text'],
    ['fuzzMaxPayloads', 'Max payloads', 'number'], ['fuzzPayloads', 'Custom payloads (one per line)', 'multiline']],
  secrets_git_history: [['secretsRepoPath', 'Repository path (required)', 'text'], ['secretsIncludeHistory', 'Include git history', 'bool'],
    ['secretsCustomPatterns', 'Custom patterns (one per line)', 'multiline']]
};

const lines = (v) => String(v || '').split('\n').map((x) => x.trim()).filter(Boolean);
export const normalizeScanInput = (value) => {
  const raw = String(value || '').trim();
  if (!raw) return '';
  return /^https?:\/\//i.test(raw) ? raw : `http://${raw}`;
};

// One POST body per scanned target. Throws a readable Error when a required field is missing.
export function buildScanRequests({ scanType, options, authProfileId, userTargetId, targets }) {
  if (!targets.length) throw new Error('Choose at least one address to scan.');
  const base = {};
  if (scanType.startsWith('web_') || scanType === 'http_basic') Object.assign(base, options);
  if (scanType === 'api_openapi') Object.assign(base, { spec_urls: [options.openapiPath || '/openapi.json'], enable_oast: Boolean(options.enableOast) });
  if (scanType === 'api_active_security') Object.assign(base, { base_url: '', timeout: 10, rate_limit_burst: 8 });
  if (scanType === 'graphql_security') Object.assign(base, { graphql_endpoint: options.graphqlEndpoint || '/graphql', enable_oast: Boolean(options.enableOast) });
  if (scanType === 'web_active_fuzz') {
    Object.assign(base, {
      fuzz_type: options.fuzzType || 'sqli', method: options.fuzzMethod || 'GET', param_name: options.fuzzParam || 'q',
      custom_payloads: lines(options.fuzzPayloads), max_payloads: Number(options.fuzzMaxPayloads || 120)
    });
  }
  if (scanType === 'secrets_git_history') {
    if (!String(options.secretsRepoPath || '').trim()) throw new Error('A repository path is required for the secrets git scan.');
    Object.assign(base, { repo_path: options.secretsRepoPath, include_git_history: Boolean(options.secretsIncludeHistory), custom_patterns: lines(options.secretsCustomPatterns) });
  }
  if (authProfileId) base.auth_profile_id = Number(authProfileId);
  return targets.map((t) => {
    const url = normalizeScanInput(t.value);
    const opts = { ...base, target_url: url, selected_asset_id: t.id === 'root' ? null : t.id, selected_asset_label: t.label || t.value };
    if (scanType === 'api_active_security') opts.base_url = String(options.openapiPath || '').trim() || url;
    return { user_target: userTargetId, scan_type: scanType, options: opts };
  });
}

// ---- JS discovery results (built from the scan's findings) ----
export function buildJsInsights(findings) {
  const endpoints = new Map(); const params = new Map(); const paths = new Map(); const secrets = new Map();
  const sources = new Set(); const errors = []; let summary = null;
  findings.forEach((f) => {
    if (f.tool !== 'web_js_discovery') return;
    const raw = f.raw_data || {};
    if (raw.source_url) sources.add(raw.source_url);
    if (raw.endpoint) endpoints.set(raw.endpoint, raw.source_url || '');
    if (raw.parameter) { if (!params.has(raw.parameter)) params.set(raw.parameter, new Set()); if (raw.endpoint) params.get(raw.parameter).add(raw.endpoint); }
    if (raw.path) paths.set(`${raw.path}|${raw.status_code || ''}`, { path: raw.path, status_code: raw.status_code || '-' });
    if (raw.secret_type) secrets.set(raw.secret_type, (secrets.get(raw.secret_type) || 0) + 1);
    if (raw.error) errors.push({ url: raw.url || raw.source_url || '-', error: raw.error });
    if (f.title === 'JS discovery summary') summary = raw;
  });
  const out = {
    endpoints: [...endpoints].map(([endpoint, source_url]) => ({ endpoint, source_url })),
    parameters: [...params].map(([name, set]) => ({ name, endpoints: [...set] })),
    sensitive_paths: [...paths.values()],
    secrets: [...secrets].map(([type, count]) => ({ type, count })),
    sources: [...sources], errors
  };
  out.summary = summary || {
    processed: sources.size, endpoints: out.endpoints.length, parameters: out.parameters.length,
    secrets: out.secrets.reduce((n, s) => n + s.count, 0), sensitive_paths: out.sensitive_paths.length
  };
  return out;
}

// Each dataset: file name, json value, csv headers and rows.
export const jsDatasets = (i) => [
  { file: 'endpoints', label: 'Endpoints', json: i.endpoints, headers: ['endpoint', 'source_url'], rows: i.endpoints.map((x) => ({ endpoint: x.endpoint, source_url: x.source_url || '' })) },
  { file: 'parameters', label: 'Parameters', json: i.parameters, headers: ['parameter', 'endpoints_count', 'endpoints'],
    rows: i.parameters.map((x) => ({ parameter: x.name, endpoints_count: x.endpoints.length, endpoints: x.endpoints.join(' | ') })) },
  { file: 'sensitive_paths', label: 'Sensitive paths', json: i.sensitive_paths, headers: ['path', 'status_code'], rows: i.sensitive_paths.map((x) => ({ path: x.path || '', status_code: x.status_code || '' })) },
  { file: 'secret_types', label: 'Secret types', json: i.secrets, headers: ['secret_type', 'count'], rows: i.secrets.map((x) => ({ secret_type: x.type || '', count: x.count || 0 })) }
];

const csvCell = (v) => { const t = String(v ?? ''); return /[",\n]/.test(t) ? `"${t.replace(/"/g, '""')}"` : t; };
export const toCsv = (headers, rows) => [headers.join(','), ...rows.map((r) => headers.map((h) => csvCell(r[h])).join(','))].join('\n');

export function saveBlob(filename, blob) {
  const url = window.URL.createObjectURL(blob);
  const a = Object.assign(document.createElement('a'), { href: url, download: filename });
  document.body.appendChild(a); a.click(); document.body.removeChild(a);
  window.URL.revokeObjectURL(url);
}
export const saveText = (filename, text, type) => saveBlob(filename, new Blob([text], { type: type || 'text/plain;charset=utf-8' }));

export function jsBundle(scan, insights, findings) {
  return {
    metadata: {
      exported_at: new Date().toISOString(), scan_id: scan.id, scan_type: scan.scan_type, phase: scan.phase, status: scan.status,
      target_value: scan.target_value || '', started_at: scan.started_at || null, finished_at: scan.finished_at || null
    },
    summary: insights.summary || {}, endpoints: insights.endpoints, parameters: insights.parameters, sensitive_paths: insights.sensitive_paths,
    secret_types: insights.secrets, sources: insights.sources, fetch_errors: insights.errors,
    findings: findings.filter((f) => f.tool === 'web_js_discovery').map((f) => ({
      id: f.id, title: f.title, description: f.description, severity: f.severity, evidence: f.evidence || '', raw_data: f.raw_data || {}, created_at: f.created_at
    }))
  };
}

export async function jsBundleZip(bundle, insights) {
  const id = bundle.metadata.scan_id;
  const zip = new JSZip();
  const root = zip.folder(`js-discovery-scan-${id}`);
  const json = (v) => JSON.stringify(v, null, 2);
  root.file('bundle.json', json(bundle));
  root.file('metadata.json', json(bundle.metadata));
  root.file('summary.json', json(bundle.summary));
  root.file('sources.json', json(bundle.sources));
  root.file('fetch_errors.json', json(bundle.fetch_errors));
  root.file('findings.json', json(bundle.findings));
  jsDatasets(insights).forEach((d) => { root.file(`${d.file}.json`, json(d.json)); root.file(`${d.file}.csv`, toCsv(d.headers, d.rows)); });
  root.file('README.txt', ['FuzzScan JS Discovery Export', `Scan ID: ${id}`, `Target: ${bundle.metadata.target_value || '-'}`, `Exported at: ${bundle.metadata.exported_at}`].join('\n'));
  return zip.generateAsync({ type: 'blob' });
}
